import { CHROME_Z_INDEX } from "@/contexts/ModalContext";
import { BasicInfo } from "@/types";
import { useEffect, useState } from "react";
import { Avatar, ChromeBar, Heading } from "@/components/ui";
import { cn } from "@/lib/cn";
import { Storefront } from "@/icons";
import { ExpandingSearch } from "@/components/search/ExpandingSearch";

/**
 * Fixed top bar on `/menu`. Like delivery HamburgerMenu but without the side
 * drawer — small logo + name (after scroll > 200) and search on the right.
 * When search is open, the input replaces the whole bar.
 */
export function MenuTopBar({
  basicInfo,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Buscar...",
}: {
  basicInfo: BasicInfo;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [showCompanyInfo, setShowCompanyInfo] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowCompanyInfo(window.scrollY > 200);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const logoUrl = basicInfo?.logoImage?.URL;

  return (
    <div style={{ zIndex: CHROME_Z_INDEX.bar }} className="fixed top-0 left-0 right-0 w-full max-w-shell m-auto">
      <ChromeBar>
        <div className="flex w-full items-center justify-between gap-3 px-4">
          <div
            className={cn(
              "flex min-w-0 items-center gap-2 transition-[opacity,transform] duration-slow ease-standard",
              showCompanyInfo && !searchOpen
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-2 pointer-events-none",
            )}
          >
            {logoUrl ? (
              <Avatar src={logoUrl} alt="logo" crc={basicInfo?.logoImage?.["CRC-32"]} size="sm" />
            ) : (
              <Avatar alt="logo" icon={Storefront} size="sm" />
            )}
            <Heading level={2} size="card" truncate className="min-w-0">
              {basicInfo.name}
            </Heading>
          </div>

          <ExpandingSearch
            open={searchOpen}
            onOpenChange={setSearchOpen}
            value={searchValue}
            onValueChange={onSearchChange}
            placeholder={searchPlaceholder}
          />
        </div>
      </ChromeBar>
    </div>
  );
}
